import { useMemo } from 'react';
import { formatCurrency, formatPercent } from '../SharedAccountBody';
import ComparisonCardSkeleton from './ComparisonCardSkeleton';
import ComparisonHoverTooltip from './ComparisonHoverTooltip';
import { isComparisonContentLoading } from './comparisonLoading';
import type { ComparisonAccount } from './comparisonTypes';
import {
  moverRowTooltipContent,
  positionRowTooltipContent,
} from './comparisonTooltipContent';
import {
  buildDisagreements,
  buildPositionDiff,
  lookupCompanyName,
  lookupLastSoldAt,
  lookupPositionMeta,
  topMovers,
  type MoverRow,
} from './comparisonUtils';

const TOP_N = 3;

const CARD_CLASS =
  'rounded-[12px] border border-[color:var(--nq-line)] bg-[color:var(--nq-surface-card)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

const TICKER = 'font-mono text-[0.85rem] font-semibold tracking-[0.02em]';
const CHIP_DOT = 'inline-block h-2 w-2 shrink-0 rounded-full';
const TAG_BASE =
  'ml-1.5 inline-block whitespace-nowrap rounded-[6px] border border-white/10 bg-white/[0.06] px-[7px] py-0.5 text-[0.68rem] font-semibold uppercase leading-[1.3] tracking-[0.06em] text-white/70';
const TAG_SPREAD =
  'bg-primary/12 border-primary/35 text-[color:#8bd6f1]';

const MoverList = ({
  title,
  rows,
  variant,
  account,
  accounts,
}: {
  title: string;
  rows: MoverRow[];
  variant: 'gain' | 'loss';
  account: ComparisonAccount;
  accounts: ComparisonAccount[];
}) => {
  const valueTone = variant === 'gain' ? 'text-positive' : 'text-negative';
  return (
    <div>
      <span className="mb-0.5 block text-[0.7rem] uppercase tracking-[0.06em] text-white/55">
        {title}
      </span>
      {rows.length === 0 ? (
        <span className="text-[0.85rem] text-white/35">—</span>
      ) : (
        <ul className="flex list-none flex-col gap-0.5">
          {rows.map((row) => {
            const pct = row.unrealizedPLPct;
            const amount = row.unrealizedPL;
            const value =
              pct !== undefined
                ? formatPercent(pct)
                : amount !== undefined
                  ? `${amount >= 0 ? '+' : ''}${formatCurrency(amount)}`
                  : '—';
            const cell = lookupPositionMeta(
              accounts,
              account.accountId,
              row.symbol,
            );
            return (
              <li key={row.symbol} className="flex items-center justify-between text-[0.85rem]">
                <ComparisonHoverTooltip
                  content={moverRowTooltipContent(
                    row.symbol,
                    cell,
                    account.label,
                    lookupCompanyName(accounts, row.symbol),
                  )}
                >
                  <span className="inline-flex w-full cursor-help items-center justify-between gap-2">
                    <span className={TICKER}>{row.symbol}</span>
                    <span className={`tabular-nums font-medium ${valueTone}`}>
                      {value}
                    </span>
                  </span>
                </ComparisonHoverTooltip>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ComparisonWinnersLosers = ({
  accounts,
}: {
  accounts: ComparisonAccount[];
}) => {
  const diff = useMemo(() => buildPositionDiff(accounts), [accounts]);
  const disagreements = useMemo(
    () => buildDisagreements(diff, accounts.length),
    [diff, accounts.length],
  );

  if (isComparisonContentLoading(accounts)) {
    return <ComparisonCardSkeleton title="Winners & losers" lines={8} />;
  }

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-col">
        <h3 className="text-[1.05rem] font-semibold text-foreground">
          Winners &amp; losers
        </h3>
        <p className="mt-1 text-[0.85rem] text-white/55">
          Top movers per portfolio (unrealized P/L) and where the portfolios
          disagree most.
        </p>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
        {accounts.map((acc) => {
          const { gainers, losers } = topMovers(acc.content, TOP_N);
          return (
            <div
              key={acc.accountId}
              className="flex flex-col gap-2 rounded-[10px] border border-white/[0.07] bg-white/[0.015] px-3 py-2.5"
            >
              <div className="flex items-center gap-1.5 border-b border-white/[0.06] pb-1.5">
                <span
                  className={CHIP_DOT}
                  style={{ backgroundColor: acc.color }}
                  aria-hidden
                />
                <span className="text-[0.85rem] font-semibold">{acc.label}</span>
              </div>
              <MoverList
                title="Top gainers"
                rows={gainers}
                variant="gain"
                account={acc}
                accounts={accounts}
              />
              <MoverList
                title="Top losers"
                rows={losers}
                variant="loss"
                account={acc}
                accounts={accounts}
              />
            </div>
          );
        })}
      </div>

      {disagreements.length > 0 && (
        <div className="mt-4 border-t border-white/[0.06] pt-3">
          <div className="mb-2">
            <span className="block text-[0.95rem] font-semibold">
              Where portfolios disagree
            </span>
            <span className="text-[0.8rem] text-white/55">
              Symbols held by some but not all, or with the largest weight
              spread.
            </span>
          </div>
          <ul className="flex list-none flex-col gap-1.5">
            {disagreements.map((row) => (
              <li
                key={row.symbol}
                className="flex flex-wrap items-center gap-1.5 rounded-[8px] border border-white/[0.06] bg-[color:var(--nq-surface-card)] px-2 py-1.5"
              >
                <ComparisonHoverTooltip
                  content={positionRowTooltipContent(
                    row.symbol,
                    row.cells[accounts[0]!.accountId],
                    undefined,
                    lookupLastSoldAt(accounts, row.symbol),
                    lookupCompanyName(accounts, row.symbol),
                  )}
                >
                  <span className={TICKER}>{row.symbol}</span>
                </ComparisonHoverTooltip>
                <span className={TAG_BASE}>
                  {row.holdersCount}/{row.totalCount} holders
                </span>
                {row.weightSpread > 0 && (
                  <span className={`${TAG_BASE} ${TAG_SPREAD}`}>
                    {row.weightSpread.toFixed(1)} pp spread
                  </span>
                )}
                <span className="ml-auto inline-flex flex-wrap gap-1.5">
                  {accounts.map((acc) => {
                    const cell = row.cells[acc.accountId];
                    return (
                      <ComparisonHoverTooltip
                        key={acc.accountId}
                        content={positionRowTooltipContent(
                          row.symbol,
                          cell,
                          acc.label,
                          lookupLastSoldAt(accounts, row.symbol),
                          lookupCompanyName(accounts, row.symbol),
                        )}
                      >
                        <span
                          className={`inline-flex items-center gap-1 rounded-[6px] bg-white/[0.04] px-1.5 py-px text-[0.78rem] tabular-nums${
                            cell ? '' : ' opacity-45'
                          }`}
                        >
                          <span
                            className={CHIP_DOT}
                            style={{ backgroundColor: acc.color }}
                            aria-hidden
                          />
                          {cell ? `${cell.weightPct.toFixed(1)}%` : '—'}
                        </span>
                      </ComparisonHoverTooltip>
                    );
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ComparisonWinnersLosers;
